import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Antenna, Plus, Save, Trash2, X as XIcon, CircleDot, Copy, Check, Globe } from "lucide-react";
import { BridgeApi, AuthApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { PageHeader } from "../components/PageScaffold";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { PlatformBadge } from "../components/badges";
import { relativeTime } from "../lib/format";
import { cn } from "../lib/cn";
import type { Channel, ChannelPlatform, ChannelStatus, InboundACL, Route } from "../lib/types";
import {
  PlatformPicker,
  TelegramWizard,
  GchatWizard,
  WhatsAppWizard,
  AsanaWizard,
  EmailWizard,
  SlackWizard,
  ChannelDefaultsFields,
} from "./ChannelWizards";

const PLATFORMS: ChannelPlatform[] = ["whatsapp", "gchat", "telegram", "asana", "email", "slack"];
const STATUSES: ChannelStatus[] = ["pending", "connected", "error", "disabled"];

export default function Channels() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [wizardPlatform, setWizardPlatform] = useState<ChannelPlatform | "picker" | null>(null);

  const { data: channels = [], isLoading } = useQuery({
    queryKey: ["bridge", "channels"],
    queryFn: BridgeApi.channels,
  });
  const { data: cfg } = useQuery({ queryKey: qk.bridge.config, queryFn: BridgeApi.config });
  const personaNames = useMemo(() => cfg ? Object.keys(cfg.personas) : [], [cfg]);

  const isCreating = selectedId === "__new__";
  const selectedChannel = selectedId && !isCreating
    ? channels.find((c) => String(c.id) === selectedId) ?? null
    : null;

  function startCreate() {
    setSelectedId("__new__");
    setWizardPlatform("picker");
  }

  function closeWizard() {
    setWizardPlatform(null);
    setSelectedId(null);
  }

  function onWizardCreated() {
    qc.invalidateQueries({ queryKey: ["bridge", "channels"] });
    qc.invalidateQueries({ queryKey: qk.bridge.config });
    closeWizard();
    toast.success("Channel created");
  }

  const deleteMut = useMutation({
    mutationFn: (id: number) => BridgeApi.deleteChannel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bridge", "channels"] });
      qc.invalidateQueries({ queryKey: qk.bridge.config });
      setSelectedId(null);
      toast.success("Channel deleted");
    },
    onError: (err) => toast.error("Delete failed", String(err)),
  });

  if (isLoading) return <CenteredSpinner />;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Channels"
        icon={Antenna}
        subtitle={`${channels.length} channel${channels.length === 1 ? "" : "s"} configured`}
        actions={
          <Button size="sm" onClick={startCreate}>
            <Plus className="size-3.5" />
            New channel
          </Button>
        }
      />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<Channel>
          items={channels}
          itemKey={(ch) => String(ch.id)}
          selectedId={selectedId}
          onSelect={(id) => { setSelectedId(id); setWizardPlatform(null); }}
          itemHeight={68}
          emptyState={
            <EmptyState
              icon={<Antenna className="size-8" />}
              title="No channels yet"
              description="A channel binds a platform credential to one or more chats."
            />
          }
          renderItem={({ item }) => <ChannelRow ch={item} />}
          renderCreating={() => {
            const wizardRoutes = cfg?.routes ?? [];
            const wizardMcp = cfg?.mcp_servers_by_persona ?? {};
            if (wizardPlatform === "telegram") {
              return <TelegramWizard personaNames={personaNames} routes={wizardRoutes} mcpByPersona={wizardMcp} onCreated={onWizardCreated} onCancel={closeWizard} />;
            }
            if (wizardPlatform === "gchat") {
              return <GchatWizard personaNames={personaNames} routes={wizardRoutes} mcpByPersona={wizardMcp} onCreated={onWizardCreated} onCancel={closeWizard} />;
            }
            if (wizardPlatform === "whatsapp") {
              return <WhatsAppWizard personaNames={personaNames} routes={wizardRoutes} mcpByPersona={wizardMcp} onCreated={onWizardCreated} onCancel={closeWizard} />;
            }
            if (wizardPlatform === "asana") {
              return <AsanaWizard personaNames={personaNames} routes={wizardRoutes} mcpByPersona={wizardMcp} onCreated={onWizardCreated} onCancel={closeWizard} />;
            }
            if (wizardPlatform === "email") {
              return <EmailWizard personaNames={personaNames} routes={wizardRoutes} mcpByPersona={wizardMcp} onCreated={onWizardCreated} onCancel={closeWizard} />;
            }
            if (wizardPlatform === "slack") {
              return <SlackWizard personaNames={personaNames} routes={wizardRoutes} mcpByPersona={wizardMcp} onCreated={onWizardCreated} onCancel={closeWizard} />;
            }
            // Default: platform picker (also covers wizardPlatform === "picker")
            return <PlatformPicker onPick={(p) => setWizardPlatform(p)} onCancel={closeWizard} />;
          }}
          renderDetail={() => (
            <ChannelEditor
              key={selectedId}
              channel={selectedChannel}
              isCreating={false}
              personaNames={personaNames}
              routes={cfg?.routes ?? []}
              mcpByPersona={cfg?.mcp_servers_by_persona ?? {}}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ["bridge", "channels"] });
                qc.invalidateQueries({ queryKey: qk.bridge.config });
                setSelectedId(null);
                toast.success("Channel saved");
              }}
              onDelete={() => {
                if (selectedChannel && confirm(`Delete channel '${selectedChannel.name}'? Routes bound to this channel will be orphaned.`)) {
                  deleteMut.mutate(selectedChannel.id);
                }
              }}
              onCancel={() => setSelectedId(null)}
            />
          )}
        />
      </div>
    </div>
  );
}

function ChannelRow({ ch }: { ch: Channel }) {
  const enabled = ch.enabled === 1;
  return (
    <div className={cn("relative px-3 py-2.5", !enabled && "opacity-50")}>
      <div className="flex items-center gap-2 min-w-0">
        <StatusDot status={ch.status} />
        <PlatformBadge platform={ch.platform} compact />
        <span className="text-sm font-medium text-fg truncate">{ch.name}</span>
        {!enabled && <Pill tone="neutral">disabled</Pill>}
        {ch.default_persona && (
          <Pill tone="info">→ {ch.default_persona}</Pill>
        )}
      </div>
      <div className="mt-0.5 text-[11px] text-fg-subtle font-mono truncate">
        {ch.identifier ?? "no identifier"}
      </div>
      {ch.last_error && (
        <div className="mt-0.5 text-[11px] text-danger truncate">{ch.last_error}</div>
      )}
    </div>
  );
}

function StatusDot({ status }: { status: ChannelStatus }) {
  const color: Record<ChannelStatus, string> = {
    pending: "text-fg-subtle",
    connected: "text-success",
    error: "text-danger",
    disabled: "text-fg-subtle",
  };
  return <CircleDot className={cn("size-3", color[status])} aria-label={status} />;
}

// ─── Editor ─────────────────────────────────────────────────────────────

interface DraftChannel {
  name: string;
  platform: ChannelPlatform;
  identifier: string;
  default_persona: string;
  default_engagement: "" | "all" | "mention" | "observe";
  default_assist_report_to: string;
  auto_route: boolean;
  default_mcp_servers: string[] | null;
  default_instructions: string;
  default_acl: InboundACL | "";
  status: ChannelStatus;
  enabled: boolean;
  /** Parsed credentials object (per platform shape). null = no creds yet. */
  credentials: Record<string, unknown> | null;
  /** Raw-JSON escape hatch — when set, replaces the structured editor. */
  credentialsRaw: string | null;
  config: string;      // JSON text
}

const NEW_DRAFT: DraftChannel = {
  name: "",
  platform: "whatsapp",
  identifier: "",
  default_persona: "",
  default_engagement: "",
  default_assist_report_to: "",
  auto_route: false,
  default_mcp_servers: null,
  default_instructions: "",
  default_acl: "",
  status: "pending",
  enabled: true,
  credentials: null,
  credentialsRaw: null,
  config: "",
};

function parseCredsField(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed as Record<string, unknown> : null;
  } catch { return null; }
}

// default_mcp_servers is stored as a JSON array string (null = all granted). Parse
// it into the array the picker works with; treat malformed/empty as null (= all).
function parseMcpServersField(raw: string | null | undefined): string[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : null;
  } catch { return null; }
}

function WhatsAppRelinkPanel() {
  const toast = useToast();
  const [active, setActive] = useState(false);
  const [copied, setCopied] = useState(false);
  const start = useMutation({
    mutationFn: () => BridgeApi.waRelink(),
    onSuccess: (r) => {
      if (r.error) { toast.error("Re-link failed", r.error); return; }
      setActive(true);
      toast.success("Re-link started", "Watch for the pairing code below.");
    },
    onError: (e) => toast.error("Re-link failed", String(e)),
  });
  const startFresh = useMutation({
    mutationFn: () => BridgeApi.waRelinkFresh(),
    onSuccess: (r) => {
      if (r.error) { toast.error("Re-link failed", r.error); return; }
      setActive(true);
      toast.success("Fresh re-link started", "Clean store — watch for the pairing code below.");
    },
    onError: (e) => toast.error("Re-link failed", String(e)),
  });
  const status = useQuery({
    queryKey: ["bridge", "wa-relink-status"],
    queryFn: () => BridgeApi.waRelinkStatus(),
    enabled: active,
    refetchInterval: active ? 2000 : false,
  });
  const s = status.data;
  // Stop polling a while after a terminal state.
  useEffect(() => {
    if (s && (s.status === "connected" || s.status === "failed")) {
      const t = setTimeout(() => setActive(false), 30_000);
      return () => clearTimeout(t);
    }
  }, [s?.status]);
  const tone = s?.status === "connected" ? "success" : s?.status === "failed" ? "danger" : s?.status === "awaiting_code" ? "warning" : "info";
  const busy = active && s != null && !["connected", "failed"].includes(s.status);

  return (
    <Section title="Connection — re-link">
      <p className="text-[12px] text-fg-muted">
        If WhatsApp ingestion goes down (messages stop arriving), re-link the device here. This pauses
        sync, generates an 8-char pairing code, then resumes automatically. Use <b>fresh store</b> if it
        stays "connected but no messages" (a corrupted session — re-linking the same store won't fix it).
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={() => start.mutate()} loading={start.isPending || busy} disabled={busy}>
          Re-link WhatsApp
        </Button>
        <Button size="sm" variant="secondary" onClick={() => startFresh.mutate()} loading={startFresh.isPending || busy} disabled={busy}>
          Re-link (fresh store)
        </Button>
        {s && s.status !== "idle" && <Pill tone={tone}>{s.status.replace("_", " ")}</Pill>}
      </div>
      {s?.message && s.status !== "idle" && <p className="text-[12px] text-fg-muted">{s.message}</p>}
      {s?.code && s.status === "awaiting_code" && (
        <div className="rounded-md border border-warning/40 bg-warning/10 p-3 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wide text-fg-subtle">Pairing code (expires ~160s)</div>
          <div className="flex items-center gap-3">
            <code className="font-mono text-2xl tracking-widest text-fg">{s.code}</code>
            <button
              onClick={() => { void navigator.clipboard?.writeText(s.code!); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="text-fg-subtle hover:text-fg" title="Copy code"
            >
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            </button>
          </div>
          <p className="text-[11px] text-fg-muted">
            On the account phone: WhatsApp → Settings → Linked Devices → Link a Device →
            "Link with phone number instead" → enter this code.
          </p>
        </div>
      )}
    </Section>
  );
}

function ChannelEditor({
  channel,
  isCreating,
  personaNames,
  routes,
  mcpByPersona,
  onSaved,
  onDelete,
  onCancel,
}: {
  channel: Channel | null;
  isCreating: boolean;
  personaNames: string[];
  routes: Route[];
  mcpByPersona: Record<string, string[]>;
  onSaved: () => void;
  onDelete: () => void;
  onCancel: () => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<DraftChannel>(() => {
    if (channel) {
      return {
        name: channel.name,
        platform: channel.platform,
        identifier: channel.identifier ?? "",
        default_persona: channel.default_persona ?? "",
        default_engagement: (channel.default_engagement ?? "") as DraftChannel["default_engagement"],
        default_assist_report_to: channel.default_assist_report_to ?? "",
        auto_route: channel.auto_route === 1,
        default_mcp_servers: parseMcpServersField(channel.default_mcp_servers),
        default_instructions: channel.default_instructions ?? "",
        default_acl: channel.default_acl ?? "",
        status: channel.status,
        enabled: channel.enabled === 1,
        credentials: parseCredsField(channel.credentials),
        credentialsRaw: null,
        config: channel.config ?? "",
      };
    }
    return NEW_DRAFT;
  });
  const [saving, setSaving] = useState(false);
  const [rawMode, setRawMode] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const qc = useQueryClient();

  async function provisionExisting() {
    if (!channel) return;
    setProvisioning(true);
    try {
      const dry = await BridgeApi.provisionObservers(channel.id, { dryRun: true });
      if (dry.error) { toast.error("Provision failed", dry.error); return; }
      const pending = dry.pending ?? 0;
      if (pending === 0) { toast.success("Nothing to provision", "No pending requests on this channel."); return; }
      if (!confirm(`Create ${pending} observer route(s) for this channel's pending requests, and resolve them?`)) return;
      const res = await BridgeApi.provisionObservers(channel.id, {});
      if (res.error) { toast.error("Provision failed", res.error); return; }
      toast.success(`Provisioned ${res.provisioned} observer route(s)`);
      qc.invalidateQueries({ queryKey: qk.bridge.config });
      qc.invalidateQueries({ queryKey: ["requests"] });
    } catch (e) {
      toast.error("Provision failed", String(e));
    } finally {
      setProvisioning(false);
    }
  }

  if (!channel && !isCreating) {
    return (
      <EmptyState
        icon={<Antenna className="size-8" />}
        title="Pick a channel"
        description="Select a channel from the list, or create a new one."
      />
    );
  }

  function patch(p: Partial<DraftChannel>) { setDraft((d) => ({ ...d, ...p })); }

  function parseJsonOrNull(text: string, label: string): unknown | null | "ERR" {
    const trimmed = text.trim();
    if (!trimmed) return null;
    try { return JSON.parse(trimmed); }
    catch { toast.error(`${label}: not valid JSON`); return "ERR"; }
  }

  async function save() {
    setSaving(true);
    try {
      // If we're in raw mode and the user edited the textarea, re-parse before saving.
      let creds: Record<string, unknown> | null = draft.credentials;
      if (rawMode && draft.credentialsRaw !== null) {
        try {
          const parsed = JSON.parse(draft.credentialsRaw);
          if (typeof parsed !== "object" || parsed === null) throw new Error("must be an object");
          creds = parsed;
        } catch (err) {
          toast.error("credentials: not valid JSON", String(err));
          return;
        }
      }
      const cfg = parseJsonOrNull(draft.config, "config");
      if (cfg === "ERR") return;
      const body = {
        name: draft.name,
        platform: draft.platform,
        identifier: draft.identifier || null,
        default_persona: draft.default_persona || null,
        default_engagement: draft.default_engagement || null,
        default_assist_report_to: draft.default_assist_report_to || null,
        auto_route: draft.auto_route,
        default_mcp_servers: draft.default_mcp_servers,
        default_instructions: draft.default_instructions || null,
        default_acl: (draft.default_acl || null) as InboundACL | null,
        status: draft.status,
        enabled: draft.enabled,
        credentials: creds ?? undefined,
        config: cfg ?? undefined,
      };
      if (isCreating) {
        await BridgeApi.createChannel(body);
      } else if (channel) {
        await BridgeApi.updateChannel(channel.id, body);
      }
      onSaved();
    } catch (err) {
      toast.error("Save failed", String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-center gap-3">
        <h2 className="text-base font-semibold text-fg flex-1">
          {isCreating ? "New channel" : channel?.name}
        </h2>
        {!isCreating && (
          <Button size="sm" variant="ghost" onClick={onDelete}>
            <Trash2 className="size-3.5" />
            Delete
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={onCancel}>
          <XIcon className="size-3.5" />
          Close
        </Button>
        <Button size="sm" onClick={save} loading={saving} disabled={!draft.name || !draft.platform}>
          <Save className="size-3.5" />
          Save
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
        {!isCreating && channel?.platform === "whatsapp" && <WhatsAppRelinkPanel />}
        <Section title="Identity">
          <Field label="Name">
            <input
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
              disabled={!isCreating}
              placeholder="e.g. whatsapp-personal"
              className="w-full"
            />
          </Field>
          <Field label="Platform">
            <select
              value={draft.platform}
              onChange={(e) => patch({ platform: e.target.value as ChannelPlatform })}
              disabled={!isCreating}
              className="w-full"
            >
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Identifier" hint="Phone number, bot username, space ID, etc.">
            <input
              value={draft.identifier}
              onChange={(e) => patch({ identifier: e.target.value })}
              placeholder="optional but recommended"
              className="w-full"
            />
          </Field>
        </Section>

        <Section title="Defaults for new chats" hint="Persona + engagement to apply to new chats on this channel — pre-filled in the Requests form, or auto-applied if Auto-route is on.">
          <ChannelDefaultsFields
            value={{
              default_persona: draft.default_persona,
              default_engagement: draft.default_engagement,
              default_assist_report_to: draft.default_assist_report_to,
              auto_route: draft.auto_route,
              default_mcp_servers: draft.default_mcp_servers,
              default_instructions: draft.default_instructions,
              default_acl: draft.default_acl,
            }}
            onChange={(p) => patch(p)}
            personaNames={personaNames}
            routes={routes}
            mcpByPersona={mcpByPersona}
          />
          {!isCreating && channel && channel.default_persona && channel.default_engagement === "observe" && (
            <div className="rounded-md border border-border bg-surface-2 p-2.5 space-y-1.5">
              <Button size="sm" variant="secondary" onClick={provisionExisting} loading={provisioning}>
                Provision existing requests
              </Button>
              <p className="text-[11px] text-fg-subtle">
                Backfill: create silent <span className="font-mono">{channel.default_persona}</span> observer routes for this channel's pending requests and resolve them. Auto-route only catches NEW chats — this sweeps the ones already in the inbox. Uses the channel's <em>saved</em> defaults, so save first if you just changed them.
              </p>
            </div>
          )}
        </Section>

        <Section title="Runtime">
          <Field label="Status">
            <select
              value={draft.status}
              onChange={(e) => patch({ status: e.target.value as ChannelStatus })}
              className="w-full"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Enabled">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
                className="size-3.5"
              />
              <span className="text-xs text-fg-muted">
                When off, this channel is ignored by ingest and outbound senders.
              </span>
            </label>
          </Field>
        </Section>

        <Section
          title="Credentials"
          hint="Platform-specific. Toggle raw JSON if you need fields the structured editor doesn't expose."
        >
          <div className="flex justify-end -mt-2">
            <button
              type="button"
              onClick={() => {
                if (!rawMode) {
                  patch({ credentialsRaw: draft.credentials ? JSON.stringify(draft.credentials, null, 2) : "" });
                }
                setRawMode((m) => !m);
              }}
              className="text-[11px] text-fg-subtle hover:text-fg underline"
            >
              {rawMode ? "← Structured" : "Raw JSON →"}
            </button>
          </div>
          {rawMode ? (
            <textarea
              value={draft.credentialsRaw ?? ""}
              onChange={(e) => patch({ credentialsRaw: e.target.value })}
              placeholder='{"bot_token":"..."}'
              className="w-full font-mono text-xs min-h-[120px]"
              spellCheck={false}
            />
          ) : (
            <PlatformCredentialsEditor
              platform={draft.platform}
              value={draft.credentials}
              onChange={(creds) => patch({ credentials: creds })}
            />
          )}
        </Section>

        <Section title="Config (JSON)" hint="Optional per-channel knobs (poll interval, webhook URL, etc.).">
          <textarea
            value={draft.config}
            onChange={(e) => patch({ config: e.target.value })}
            placeholder="{}"
            className="w-full font-mono text-xs min-h-[60px]"
            spellCheck={false}
          />
        </Section>

        {/* Webhook URL surfacing — for platforms whose ingest comes from an
            external HTTP POST (currently gchat; asana shows it as info even
            though our ingest polls instead). Only meaningful once the row
            exists (we need its slug). */}
        {!isCreating && channel && (channel.platform === "gchat" || channel.platform === "asana") && (
          <WebhookUrlSection channel={channel} />
        )}

        {/* Asana-only: project subscription manager (only for existing channels —
            new ones use the wizard's picker before they ever reach this editor). */}
        {!isCreating && channel && channel.platform === "asana" && (
          <AsanaSubscriptionsSection channelId={channel.id} />
        )}

        {/* Email-only: IMAP folder/label subscription manager (the ingest filter). */}
        {!isCreating && channel && channel.platform === "email" && (
          <EmailFoldersSection channelId={channel.id} />
        )}

        {/* Slack-only: which channels to watch (the ingest filter). */}
        {!isCreating && channel && channel.platform === "slack" && (
          <SlackChannelsSection channelId={channel.id} />
        )}

        {channel && (
          <div className="text-[11px] text-fg-subtle font-mono pt-2 border-t border-border">
            id={channel.id} · created {relativeTime(channel.created_at)}
            {channel.last_connected_at && ` · last connected ${relativeTime(channel.last_connected_at)}`}
            {channel.last_error && (
              <div className="mt-1 text-danger break-all">last_error: {channel.last_error}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Platform-aware credential fields. Lifts the bits that matter for each
// platform to their own input. Values are kept as a parsed object so flipping
// to "Raw JSON" round-trips cleanly.
function PlatformCredentialsEditor({
  platform,
  value,
  onChange,
}: {
  platform: ChannelPlatform;
  value: Record<string, unknown> | null;
  onChange: (next: Record<string, unknown> | null) => void;
}) {
  function set(key: string, v: unknown) {
    const next: Record<string, unknown> = { ...(value ?? {}) };
    if (v === "" || v === null || v === undefined) delete next[key];
    else next[key] = v;
    onChange(Object.keys(next).length === 0 ? null : next);
  }

  if (platform === "telegram") {
    const token = String(value?.bot_token ?? "");
    return (
      <Field label="Bot token" hint="From @BotFather. Used as the only credential for inbound polling and outbound sends.">
        <input
          value={token}
          onChange={(e) => set("bot_token", e.target.value.trim())}
          placeholder="123456789:ABC..."
          className="w-full font-mono text-xs"
          spellCheck={false}
          autoComplete="off"
        />
      </Field>
    );
  }

  if (platform === "whatsapp") {
    // Path is system-derived from channel name — not editable. Shown for
    // diagnostics only. Power users can still override via Raw JSON.
    return (
      <Field
        label="wacli session dir"
        hint="The bridge derives this path from the channel name; you can't change it here."
      >
        <code className="block text-xs text-fg-muted font-mono">
          ~/.agent-fleet/wacli-sessions/&lt;channel-name&gt;/
        </code>
      </Field>
    );
  }

  if (platform === "gchat") {
    const key = value?.service_account_key as Record<string, unknown> | undefined;
    const clientEmail = typeof key?.client_email === "string" ? key.client_email : null;
    const [text, setText] = useGchatJsonState(key);
    return (
      <Field
        label="Service account JSON"
        hint="Paste the JSON downloaded from Google Cloud Console (or upload the file). Stored as a parsed object — never as a file path."
      >
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            const trimmed = e.target.value.trim();
            if (!trimmed) { set("service_account_key", null); return; }
            try {
              const parsed = JSON.parse(trimmed);
              if (typeof parsed === "object" && parsed !== null) set("service_account_key", parsed);
            } catch { /* leave previous value */ }
          }}
          placeholder='{"type":"service_account","project_id":"...","client_email":"..."}'
          className="w-full font-mono text-xs min-h-[140px]"
          spellCheck={false}
        />
        <label className="inline-flex items-center gap-1 text-[11px] text-fg-muted cursor-pointer mt-1">
          <span className="underline">Or upload .json</span>
          <input
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const t = await f.text();
              setText(t);
              try {
                const parsed = JSON.parse(t);
                if (typeof parsed === "object" && parsed !== null) set("service_account_key", parsed);
              } catch { /* */ }
            }}
          />
        </label>
        {clientEmail && (
          <span className="block text-[11px] text-success mt-0.5">
            client_email: <code>{clientEmail}</code>
          </span>
        )}
      </Field>
    );
  }

  if (platform === "asana") {
    const pat = String(value?.personal_access_token ?? "");
    return (
      <Field label="Personal access token" hint="Asana → My Profile Settings → Apps → Create token.">
        <input
          value={pat}
          onChange={(e) => set("personal_access_token", e.target.value.trim())}
          placeholder="2/123456..."
          className="w-full font-mono text-xs"
          spellCheck={false}
          autoComplete="off"
        />
      </Field>
    );
  }

  if (platform === "email") {
    const v = value ?? {};
    const str = (k: string) => String(v[k] ?? "");
    return (
      <div className="space-y-2.5">
        <Field label="IMAP host" hint="Inbound mail server. Gmail: imap.gmail.com">
          <input value={str("imap_host")} onChange={(e) => set("imap_host", e.target.value.trim())}
            placeholder="imap.gmail.com" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
        </Field>
        <Field label="IMAP port" hint="Defaults to 993 (TLS) if left blank.">
          <input value={str("imap_port")} onChange={(e) => set("imap_port", e.target.value ? Number(e.target.value) : "")}
            placeholder="993" inputMode="numeric" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
        </Field>
        <Field label="SMTP host" hint="Outbound mail server. Gmail: smtp.gmail.com">
          <input value={str("smtp_host")} onChange={(e) => set("smtp_host", e.target.value.trim())}
            placeholder="smtp.gmail.com" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
        </Field>
        <Field label="SMTP port" hint="Defaults to 465 (TLS) if left blank.">
          <input value={str("smtp_port")} onChange={(e) => set("smtp_port", e.target.value ? Number(e.target.value) : "")}
            placeholder="465" inputMode="numeric" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
        </Field>
        <Field label="Username" hint="The full mailbox address (used for IMAP + SMTP auth).">
          <input value={str("user")} onChange={(e) => set("user", e.target.value.trim())}
            placeholder="fleet@ayana.com" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
        </Field>
        <Field label="App password" hint="Gmail: an App Password (not your account password). Requires 2FA enabled.">
          <input value={str("password")} onChange={(e) => set("password", e.target.value)} type="password"
            placeholder="••••••••••••••••" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
        </Field>
        <Field label="From (optional)" hint="From: header on outbound. Defaults to the username.">
          <input value={str("from")} onChange={(e) => set("from", e.target.value.trim())}
            placeholder='"AYANA Fleet" <fleet@ayana.com>' className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
        </Field>
      </div>
    );
  }

  if (platform === "slack") {
    const v = value ?? {};
    return (
      <Field label="User OAuth Token" hint="Slack app → OAuth & Permissions → User OAuth Token (xoxp-…). Acts as your account (reads/posts as you), no bot in channels.">
        <input value={String(v.user_token ?? "")} onChange={(e) => set("user_token", e.target.value.trim())}
          type="password" placeholder="xoxp-…" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
      </Field>
    );
  }

  return (
    <p className="text-xs text-fg-muted">No structured editor for platform <code>{platform}</code> yet — use Raw JSON.</p>
  );
}

// Local textarea state for gchat — we want to keep the user's literal text
// (including their formatting) even if it's not a complete JSON object yet.
function useGchatJsonState(initialKey: Record<string, unknown> | undefined) {
  return useState<string>(initialKey ? JSON.stringify(initialKey, null, 2) : "");
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2.5">
      <div>
        <h3 className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle">{title}</h3>
        {hint && <p className="text-[11px] text-fg-subtle mt-0.5">{hint}</p>}
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-fg-muted mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-fg-subtle mt-0.5">{hint}</span>}
    </label>
  );
}

// ── Webhook URL surface (channel edit form, platforms with HTTP-POST ingest) ──
//
// For Google Chat (and eventually Asana when we register webhooks), the
// operator has to paste a URL into the external platform's console. The
// slug is built from {platform, id} so this URL is stable across name
// renames. Shown prominently as its own panel with a one-click copy.

const PLATFORM_PREFIX: Record<string, string> = {
  whatsapp: "wa", gchat: "gc", telegram: "tg", asana: "as", email: "em", slack: "sl",
};

function webhookUrlFor(publicUrl: string, channel: Pick<Channel, "platform" | "id">): string {
  const base = publicUrl.replace(/\/$/, "");
  const slug = `${PLATFORM_PREFIX[channel.platform]}-${channel.id}`;
  return `${base}/webhook/${channel.platform}/${slug}`;
}

function WebhookUrlSection({ channel }: { channel: Channel }) {
  const { data: authState } = useQuery({ queryKey: ["auth", "state"], queryFn: AuthApi.state });
  const publicUrl = authState?.public_url ?? "";
  const [copied, setCopied] = useState(false);
  if (!publicUrl) return null;
  const url = webhookUrlFor(publicUrl, channel);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* permission denied — fall through */ }
  }

  const instructions: Record<string, React.ReactNode> = {
    gchat: (
      <>
        Paste this into the bot's HTTP endpoint in <strong>Google Cloud Console</strong> →
        <em> Google Chat API</em> → <em>Configuration</em>. Google will POST every space event here.
      </>
    ),
    asana: (
      <>
        For incoming webhooks, register this URL via Asana's
        {" "}<code>POST /webhooks</code> endpoint. Polling fallback (1 h) keeps working without it.
      </>
    ),
  };

  return (
    <Section
      title="Webhook URL"
      hint="Stable per channel — the slug never changes, so you can rename the channel freely without breaking this URL."
    >
      <div className="panel-elevated p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Globe className="size-3.5 text-fg-subtle shrink-0" />
          <code className="text-xs font-mono text-fg break-all flex-1 select-all">{url}</code>
          <button
            onClick={copy}
            className={cn(
              "inline-flex items-center gap-1 h-6 px-2 rounded text-[11px] font-medium border shrink-0",
              copied ? "bg-success-subtle text-success border-success/30" : "bg-surface-2 text-fg-muted border-border hover:text-fg",
            )}
          >
            {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="text-[11px] text-fg-subtle">{instructions[channel.platform] ?? "External webhook target for this channel."}</p>
      </div>
    </Section>
  );
}

// ── Asana subscription manager (channel edit form) ──────────────────────
//
// Loads the full project list (server fetches all pages from Asana), shows
// current subs with their counts up top, and a search-filtered picker
// below. On save:
//   - POST /channels/:id/subscriptions replaces subs in one txn
//   - server enumerates the tasks for any *removed* project and disables
//     routes whose chat_jid matches one of those tasks (the "active routes
//     get disabled too" rule). The response carries the disabled-route list
//     so we can show what changed.

function AsanaSubscriptionsSection({ channelId }: { channelId: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["bridge", "channels", channelId, "asana-projects"],
    queryFn: () => BridgeApi.channelAsanaProjects(channelId),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const { data: subsResp } = useQuery({
    queryKey: ["bridge", "channels", channelId, "subscriptions"],
    queryFn: () => BridgeApi.subscriptions(channelId),
  });

  const projects = data?.projects ?? [];
  const subs = (subsResp?.subscriptions ?? []).filter((s) => s.kind === "asana_project");
  const subscribedCount = subs.length;

  // First time the picker opens, seed the selection from the projects' subscribed flag.
  useEffect(() => {
    if (open && data?.projects && selected === null) {
      setSelected(new Set(data.projects.filter((p) => p.subscribed).map((p) => p.gid)));
    }
  }, [open, data, selected]);

  function toggle(gid: string) {
    setSelected((s) => {
      const next = new Set(s ?? []);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  }

  async function save() {
    if (!selected) return;
    setSaving(true);
    try {
      const items = projects
        .filter((p) => selected.has(p.gid))
        .map((p) => ({ external_id: p.gid, name: `${p.workspace.name} / ${p.name}` }));
      const result = await BridgeApi.setSubscriptions(channelId, "asana_project", items);
      qc.invalidateQueries({ queryKey: ["bridge", "channels", channelId, "subscriptions"] });
      qc.invalidateQueries({ queryKey: ["bridge", "channels", channelId, "asana-projects"] });
      qc.invalidateQueries({ queryKey: qk.bridge.config });  // routes may have been disabled
      if (result.disabled_routes.length > 0) {
        toast.success(
          `Saved — ${result.count} subscribed, ${result.disabled_routes.length} route(s) auto-disabled`,
          result.disabled_routes.map((r) => `• ${r.name} (${r.chat_jid})`).join("\n"),
        );
      } else {
        toast.success(`Saved — ${result.count} project${result.count === 1 ? "" : "s"} subscribed`);
      }
      setOpen(false);
      setSelected(null);
    } catch (err) {
      toast.error("Save failed", String(err));
    } finally {
      setSaving(false);
    }
  }

  const filtered = projects.filter((p) =>
    !filter ||
    p.name.toLowerCase().includes(filter.toLowerCase()) ||
    p.workspace.name.toLowerCase().includes(filter.toLowerCase()),
  );
  const projectsByWorkspace = filtered.reduce<Record<string, typeof projects>>((acc, p) => {
    (acc[p.workspace.name] ??= []).push(p);
    return acc;
  }, {});

  return (
    <Section
      title="Project subscriptions"
      hint="Pick which Asana projects this channel ingests tasks from. Removing a project also disables any routes whose chat_jid points at a task in that project."
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Pill tone={subscribedCount > 0 ? "info" : "neutral"}>
          {subscribedCount} project{subscribedCount === 1 ? "" : "s"} subscribed
        </Pill>
        <Button size="sm" variant={open ? "secondary" : "primary"} onClick={() => { setOpen(!open); setSelected(null); setFilter(""); }}>
          {open ? "Cancel" : (subscribedCount === 0 ? "Pick projects" : "Manage")}
        </Button>
        {open && (
          <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isLoading}>
            Refresh from Asana
          </Button>
        )}
      </div>

      {subscribedCount > 0 && !open && (
        <ul className="text-xs text-fg-muted mt-2 space-y-0.5 max-h-[120px] overflow-auto">
          {subs.map((s) => (
            <li key={s.external_id} className="font-mono truncate">
              {s.name ?? s.external_id}
              {s.enabled === 0 && <span className="ml-1 text-fg-subtle">(disabled)</span>}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="mt-2 panel-elevated p-3 space-y-2">
          {isLoading && <p className="text-xs text-fg-muted">Fetching projects from Asana…</p>}
          {error && <p className="text-xs text-danger">Fetch failed: {String(error)}</p>}
          {!isLoading && !error && (
            <>
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={`filter ${projects.length} projects…`}
                className="w-full text-sm"
                autoFocus
              />
              <div className="space-y-3 max-h-[340px] overflow-y-auto">
                {Object.entries(projectsByWorkspace).map(([wsName, projs]) => (
                  <div key={wsName}>
                    <div className="text-[10px] uppercase tracking-wider text-fg-subtle mb-1 sticky top-0 bg-surface-2 py-0.5">{wsName}</div>
                    {projs.map((p) => (
                      <label key={p.gid} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-surface-2 rounded px-1">
                        <input
                          type="checkbox"
                          checked={selected?.has(p.gid) ?? false}
                          onChange={() => toggle(p.gid)}
                          className="size-3.5"
                        />
                        <span className="text-xs text-fg">{p.name}</span>
                        {p.subscribed && !selected?.has(p.gid) && (
                          <span className="text-[10px] text-warning">(removing)</span>
                        )}
                      </label>
                    ))}
                  </div>
                ))}
                {filtered.length === 0 && <p className="text-xs text-fg-subtle">No projects match.</p>}
              </div>
              <div className="flex items-center gap-2 pt-1 border-t border-border">
                <p className="text-[11px] text-fg-subtle flex-1">{selected?.size ?? 0} selected</p>
                <Button size="sm" onClick={save} loading={saving} disabled={selected === null}>
                  Save subscriptions
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Section>
  );
}

// ── Email folder/label subscription manager (channel edit form) ─────────
//
// The IMAP analog of AsanaSubscriptionsSection: lists selectable folders/labels
// (Gmail labels show as IMAP folders) and persists the chosen ones as
// channel_subscriptions of kind "email_label". The bridge ingests mail only
// from subscribed folders (no subscriptions → INBOX only). No route cascade —
// email never auto-routes, so there's nothing to disable.

function EmailFoldersSection({ channelId }: { channelId: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["bridge", "channels", channelId, "email-folders"],
    queryFn: () => BridgeApi.channelEmailFolders(channelId),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const { data: subsResp } = useQuery({
    queryKey: ["bridge", "channels", channelId, "subscriptions"],
    queryFn: () => BridgeApi.subscriptions(channelId),
  });

  const folders = data?.folders ?? [];
  const subs = (subsResp?.subscriptions ?? []).filter((s) => s.kind === "email_label");
  const subscribedCount = subs.length;

  useEffect(() => {
    if (open && data?.folders && selected === null) {
      setSelected(new Set(data.folders.filter((f) => f.subscribed).map((f) => f.path)));
    }
  }, [open, data, selected]);

  function toggle(path: string) {
    setSelected((s) => {
      const next = new Set(s ?? []);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }

  async function save() {
    if (!selected) return;
    setSaving(true);
    try {
      const items = folders.filter((f) => selected.has(f.path)).map((f) => ({ external_id: f.path, name: f.path }));
      const result = await BridgeApi.setSubscriptions(channelId, "email_label", items, { cascadeDisableRoutes: false });
      qc.invalidateQueries({ queryKey: ["bridge", "channels", channelId, "subscriptions"] });
      qc.invalidateQueries({ queryKey: ["bridge", "channels", channelId, "email-folders"] });
      toast.success(`Saved — ${result.count} folder${result.count === 1 ? "" : "s"} subscribed`);
      setOpen(false);
      setSelected(null);
    } catch (err) {
      toast.error("Save failed", String(err));
    } finally {
      setSaving(false);
    }
  }

  const filtered = folders.filter((f) => !filter || f.path.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Section
      title="Folder / label subscriptions"
      hint="Pick which IMAP folders (Gmail labels) this channel ingests mail from. No subscriptions → INBOX only."
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Pill tone={subscribedCount > 0 ? "info" : "neutral"}>
          {subscribedCount > 0 ? `${subscribedCount} folder${subscribedCount === 1 ? "" : "s"} subscribed` : "INBOX only"}
        </Pill>
        <Button size="sm" variant={open ? "secondary" : "primary"} onClick={() => { setOpen(!open); setSelected(null); setFilter(""); }}>
          {open ? "Cancel" : (subscribedCount === 0 ? "Pick folders" : "Manage")}
        </Button>
        {open && (
          <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isLoading}>
            Refresh from IMAP
          </Button>
        )}
      </div>

      {subscribedCount > 0 && !open && (
        <ul className="text-xs text-fg-muted mt-2 space-y-0.5 max-h-[120px] overflow-auto">
          {subs.map((s) => (
            <li key={s.external_id} className="font-mono truncate">
              {s.name ?? s.external_id}
              {s.enabled === 0 && <span className="ml-1 text-fg-subtle">(disabled)</span>}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="mt-2 panel-elevated p-3 space-y-2">
          {isLoading && <p className="text-xs text-fg-muted">Connecting to IMAP…</p>}
          {error && <p className="text-xs text-danger">Fetch failed: {String(error)}</p>}
          {!isLoading && !error && data?.ok === false && (
            <p className="text-xs text-danger">{data.error ?? "Could not list folders."}</p>
          )}
          {!isLoading && !error && data?.ok !== false && (
            <>
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={`filter ${folders.length} folders…`}
                className="w-full text-sm"
                autoFocus
              />
              <div className="space-y-0.5 max-h-[340px] overflow-y-auto">
                {filtered.map((f) => (
                  <label key={f.path} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-surface-2 rounded px-1">
                    <input
                      type="checkbox"
                      checked={selected?.has(f.path) ?? false}
                      onChange={() => toggle(f.path)}
                      className="size-3.5"
                    />
                    <span className="text-xs text-fg font-mono">{f.path}</span>
                    {f.subscribed && !selected?.has(f.path) && (
                      <span className="text-[10px] text-warning">(removing)</span>
                    )}
                  </label>
                ))}
                {filtered.length === 0 && <p className="text-xs text-fg-subtle">No folders match.</p>}
              </div>
              <div className="flex items-center gap-2 pt-1 border-t border-border">
                <p className="text-[11px] text-fg-subtle flex-1">{selected?.size ?? 0} selected</p>
                <Button size="sm" onClick={save} loading={saving} disabled={selected === null}>
                  Save subscriptions
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Section>
  );
}

// ── Slack channel-watch manager (the ingest filter) ─────────────────────
// Lists the channels the user is in (via the stored token); the ones you pick
// are polled. No subscriptions → nothing ingested. Mirrors EmailFoldersSection.
function SlackChannelsSection({ channelId }: { channelId: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["bridge", "channels", channelId, "slack-channels"],
    queryFn: () => BridgeApi.channelSlackChannels(channelId),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const { data: subsResp } = useQuery({
    queryKey: ["bridge", "channels", channelId, "subscriptions"],
    queryFn: () => BridgeApi.subscriptions(channelId),
  });

  const channels = data?.channels ?? [];
  const subs = (subsResp?.subscriptions ?? []).filter((s) => s.kind === "slack_channel");
  const subscribedCount = subs.length;

  useEffect(() => {
    if (open && data?.channels && selected === null) {
      setSelected(new Set(data.channels.filter((c) => c.subscribed).map((c) => c.id)));
    }
  }, [open, data, selected]);

  function toggle(id: string) {
    setSelected((s) => { const next = new Set(s ?? []); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  async function save() {
    if (!selected) return;
    setSaving(true);
    try {
      const items = channels.filter((c) => selected.has(c.id)).map((c) => ({ external_id: c.id, name: c.name }));
      const result = await BridgeApi.setSubscriptions(channelId, "slack_channel", items, { cascadeDisableRoutes: false });
      qc.invalidateQueries({ queryKey: ["bridge", "channels", channelId, "subscriptions"] });
      qc.invalidateQueries({ queryKey: ["bridge", "channels", channelId, "slack-channels"] });
      toast.success(`Saved — ${result.count} channel${result.count === 1 ? "" : "s"} watched`);
      setOpen(false);
      setSelected(null);
    } catch (err) { toast.error("Save failed", String(err)); }
    finally { setSaving(false); }
  }

  const filtered = channels.filter((c) => !filter || c.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Section
      title="Channel subscriptions"
      hint="Pick which Slack channels this account ingests messages from (polled). No subscriptions → nothing is ingested."
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Pill tone={subscribedCount > 0 ? "info" : "neutral"}>
          {subscribedCount > 0 ? `${subscribedCount} channel${subscribedCount === 1 ? "" : "s"} watched` : "none watched"}
        </Pill>
        <Button size="sm" variant={open ? "secondary" : "primary"} onClick={() => { setOpen(!open); setSelected(null); setFilter(""); }}>
          {open ? "Cancel" : (subscribedCount === 0 ? "Pick channels" : "Manage")}
        </Button>
        {open && <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isLoading}>Refresh from Slack</Button>}
      </div>

      {subscribedCount > 0 && !open && (
        <ul className="text-xs text-fg-muted mt-2 space-y-0.5 max-h-[120px] overflow-auto">
          {subs.map((s) => (
            <li key={s.external_id} className="font-mono truncate">#{s.name ?? s.external_id}{s.enabled === 0 && <span className="ml-1 text-fg-subtle">(disabled)</span>}</li>
          ))}
        </ul>
      )}

      {open && (
        <div className="mt-2 panel-elevated p-3 space-y-2">
          {isLoading && <p className="text-xs text-fg-muted">Loading channels from Slack…</p>}
          {error && <p className="text-xs text-danger">Fetch failed: {String(error)}</p>}
          {!isLoading && !error && data?.ok === false && <p className="text-xs text-danger">{data.error ?? "Could not list channels."}</p>}
          {!isLoading && !error && data?.ok !== false && (
            <>
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={`filter ${channels.length} channels…`} className="w-full text-sm" autoFocus />
              <div className="space-y-0.5 max-h-[340px] overflow-y-auto">
                {filtered.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-surface-2 rounded px-1">
                    <input type="checkbox" checked={selected?.has(c.id) ?? false} onChange={() => toggle(c.id)} className="size-3.5" />
                    <span className="text-xs text-fg font-mono">#{c.name}</span>
                    {c.subscribed && !selected?.has(c.id) && <span className="text-[10px] text-warning">(removing)</span>}
                  </label>
                ))}
                {filtered.length === 0 && <p className="text-xs text-fg-subtle">No channels match.</p>}
              </div>
              <div className="flex items-center gap-2 pt-1 border-t border-border">
                <p className="text-[11px] text-fg-subtle flex-1">{selected?.size ?? 0} selected</p>
                <Button size="sm" onClick={save} loading={saving} disabled={selected === null}>Save subscriptions</Button>
              </div>
            </>
          )}
        </div>
      )}
    </Section>
  );
}
